import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RulePicker } from './RulePicker.js';

interface ConflictRow {
  readonly conflictId: string;
  readonly ruleCode: string | null;
  readonly ruleName: string | null;
  readonly conflictingRuleCode: string | null;
  readonly conflictingRuleName: string | null;
  readonly conflictType: string;
  readonly severity: string;
  readonly status: string;
}

const ruleRef = (code: string | null, name: string | null): string =>
  [code, name].filter(Boolean).join(' \u2014 ');

export function M24ConflictManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [conflicts, setConflicts] = useState<ConflictRow[]>([]);
  const [ruleId, setRuleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ConflictRow[] }>('/v1/rule-conflicts', { token })
      .then((r) => { if (alive) { setConflicts(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => load(), [load]);

  function onDetect(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setBusy(true);
    apiRequest<{ data: ConflictRow[] }>(`/v1/rule-conflicts/detect/${encodeURIComponent(ruleId)}`, {
      method: 'POST',
      token,
    })
      .then((r) => { setMessage(t('m24.conflict.msgDetected', { count: r.data.length })); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  function onResolve(c: ConflictRow): void {
    setError(null);
    setMessage(null);
    setBusy(true);
    apiRequest<{ data: ConflictRow }>(`/v1/rule-conflicts/${c.conflictId}/resolve`, {
      method: 'POST',
      token,
      body: { resolution: 'Resolved via console' },
    })
      .then(() => { setMessage(t('m24.conflict.msgResolved', { rule: ruleRef(c.ruleCode, c.ruleName) })); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  return (
    <main aria-label="conflict-management">
      <h1>{t('m24.conflict.title')}</h1>
      <form aria-label="detect-form" onSubmit={onDetect}>
        <RulePicker label={t('m24.conflict.ruleId')} value={ruleId} onPick={(id) => setRuleId(id)} />
        <button type="submit" disabled={busy || !ruleId.trim()}>{t('m24.conflict.detect')}</button>
      </form>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m24.conflict.loading')}</p> : (
        <table aria-label="conflict-table">
          <thead>
            <tr><th>{t('m24.conflict.colType')}</th><th>{t('m24.conflict.colRule')}</th><th>{t('m24.conflict.colConflictingRule')}</th><th>{t('m24.conflict.colSeverity')}</th><th>{t('m24.conflict.colStatus')}</th><th>{t('m24.conflict.colAction')}</th></tr>
          </thead>
          <tbody>
            {conflicts.map((c) => (
              <tr key={c.conflictId}>
                <td>{c.conflictType}</td>
                <td>{ruleRef(c.ruleCode, c.ruleName)}</td>
                <td>{c.conflictingRuleCode ? ruleRef(c.conflictingRuleCode, c.conflictingRuleName) : '—'}</td>
                <td>{c.severity}</td>
                <td>{c.status}</td>
                <td>
                  {c.status === 'DETECTED' && (
                    <button type="button" disabled={busy} onClick={() => onResolve(c)}>{t('m24.conflict.resolve')}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
